/**
 * Statistical calculation utilities for refinery assay data
 * Shared across XrfBarSection, FireBarSection, and ResultsSection components
 */

import { formatToPrecision } from '@/util/formatting'
import { DEFAULT_WEIGHT_PRECISION } from '@/util/refinery/constants'

/**
 * Filters array to only numeric values (excludes null, undefined, NaN, strings)
 * @param {Array} values - Array that may contain mixed types
 * @returns {Array} Array containing only numeric values
 */
export const filterNumericValues = (values) => {
  if (!Array.isArray(values)) return []
  return values.filter(v => typeof v === 'number' && !isNaN(v))
}

/**
 * Calculates average of numeric values
 * @param {Array} values - Array of values (may contain non-numeric)
 * @returns {number|null} Average value or null if no valid values
 */
export const calculateAverage = (values) => {
  const nums = filterNumericValues(values)
  if (!nums.length) return null
  
  return nums.reduce((a, b) => a + b, 0) / nums.length
}

/**
 * Calculates minimum of numeric values
 * @param {Array} values - Array of values (may contain non-numeric)
 * @returns {number|null} Minimum value or null if no valid values
 */
export const calculateMinimum = (values) => {
  const nums = filterNumericValues(values)
  if (!nums.length) return null
  
  return Math.min(...nums)
}

/**
 * Calculates maximum of numeric values (no formatting for comparisons)
 * @param {Array} values - Array of values (may contain non-numeric)
 * @returns {number|null} Maximum value or null if no valid values
 */
export const calculateMaximum = (values) => {
  const nums = filterNumericValues(values)
  if (!nums.length) return null
  
  return Math.max(...nums)
}

/**
 * Calculates spread (difference between max and min) of numeric values
 * @param {Array} values - Array of values (may contain non-numeric)
 * @returns {number|null} Spread value or null if insufficient data
 */
export const calculateSpread = (values) => {
  const nums = filterNumericValues(values)
  if (nums.length < 2) return null
  
  const min = Math.min(...nums)
  const max = Math.max(...nums)
  return max - min
}

/**
 * Calculates spread and returns formatted string for display
 * Uses formatToPrecision for consistent decimal formatting
 * @param {Array} values - Array of values (may contain non-numeric)
 * @param {number} precision - Number of decimal places (default: 3)
 * @returns {string} Formatted spread or empty string if insufficient data
 */
export const calculateSpreadDisplay = (values, precision = 3) => {
  const spread = calculateSpread(values)
  if (spread === null) return ''
  return formatToPrecision(spread, precision)
}

/**
 * Checks if spread exceeds threshold value
 * @param {Array} values - Array of values (may contain non-numeric)
 * @param {number} threshold - Threshold value for direct comparison (e.g., 3 for 3 percentage points)
 * @returns {boolean} True if spread exceeds threshold
 */
export const spreadExceedsThreshold = (values, threshold) => {
  const nums = filterNumericValues(values)
  if (nums.length < 2) return false
  
  const min = Math.min(...nums)
  const max = Math.max(...nums)
  const spread = max - min
  
  return spread > threshold
}

/**
 * Calculate preliminary ounces from average percentage and process weight
 * Uses formatToPrecision for consistent decimal formatting
 * @param {string|number} averagePercentage - Average percentage (e.g., "95.50" or 95.50)
 * @param {number|null} processWeight - Process weight in troy ounces
 * @param {number} precision - Number of decimal places for weight (default: DEFAULT_WEIGHT_PRECISION)
 * @returns {string} Formatted preliminary ounces or empty string if invalid inputs
 */
export const calculatePreliminaryOunces = (averagePercentage, processWeight, precision = DEFAULT_WEIGHT_PRECISION) => {
  if (processWeight == null || processWeight === '') return ''
  if (averagePercentage == null || averagePercentage === '') return ''
  
  const avg = typeof averagePercentage === 'number' ? averagePercentage : parseFloat(averagePercentage)
  if (isNaN(avg)) return ''
  
  const ounces = (avg / 100) * processWeight
  return formatToPrecision(ounces, precision)
}

